export type UserRole = "admin" | "agency_owner" | "member";

export const MEMBER_OFFER_TAB_IDS = [
  "dashboard",
  "leaderboard",
  "daily-report",
  "acquisition",
  "closers",
  "setters",
  "content",
  "leads",
  "utm-builder",
  "eod-builder",
  "rev-projector",
] as const;

export type MemberOfferTabId = (typeof MEMBER_OFFER_TAB_IDS)[number];

export interface OfferAccessEntry {
  offer: string;
  tabs: MemberOfferTabId[];
}

export const DEFAULT_MEMBER_TABS: MemberOfferTabId[] = ["dashboard", "leaderboard", "content", "leads"];

export function normalizeOfferName(value: string | null | undefined): string {
  return (value || "").trim();
}

export function normalizeOfferAccess(
  offerAccess: unknown,
  validOffers: string[] = []
): OfferAccessEntry[] {
  if (!Array.isArray(offerAccess)) return [];

  const validOfferSet = new Set(validOffers.map((offer) => normalizeOfferName(offer)).filter(Boolean));
  const allowedTabs = new Set<string>(MEMBER_OFFER_TAB_IDS);
  const seenOffers = new Set<string>();
  const normalized: OfferAccessEntry[] = [];

  for (const entry of offerAccess) {
    if (!entry || typeof entry !== "object") continue;

    const offer = normalizeOfferName((entry as { offer?: string }).offer);
    if (!offer || seenOffers.has(offer)) continue;
    if (validOfferSet.size > 0 && !validOfferSet.has(offer)) continue;

    const rawTabs: unknown[] = Array.isArray((entry as { tabs?: unknown[] }).tabs)
      ? (entry as { tabs?: unknown[] }).tabs ?? []
      : [];

    const tabs = Array.from(
      new Set(
        rawTabs
          .filter((tab): tab is string => typeof tab === "string")
          .map((tab) => tab.trim())
          .filter((tab): tab is MemberOfferTabId => allowedTabs.has(tab))
      )
    );

    if (tabs.length === 0) continue;

    seenOffers.add(offer);
    normalized.push({ offer, tabs });
  }

  return normalized;
}

export function validateOfferAccess(
  role: UserRole,
  offerAccess: unknown,
  validOffers: string[] = []
): { valid: boolean; error?: string; data: OfferAccessEntry[] } {
  const normalized = normalizeOfferAccess(offerAccess, validOffers);

  if (role !== "member") {
    return { valid: true, data: normalized };
  }

  if (normalized.length === 0) {
    return {
      valid: false,
      error: "Members must have at least one assigned offer",
      data: [],
    };
  }

  for (const entry of normalized) {
    if (!entry.tabs.includes("dashboard")) {
      return {
        valid: false,
        error: `Assigned offer "${entry.offer}" must include dashboard access`,
        data: normalized,
      };
    }
  }

  return { valid: true, data: normalized };
}

export function isElevatedRole(role: UserRole | null | undefined): boolean {
  return role === "admin" || role === "agency_owner";
}

export function getAllowedOffersForUser(role: UserRole | null | undefined, offerAccess: OfferAccessEntry[] = []): string[] {
  if (isElevatedRole(role)) return [];
  return offerAccess.map((entry) => entry.offer);
}

export function canUserAccessAgency(role: UserRole | null | undefined): boolean {
  return isElevatedRole(role);
}

export function canUserAccessOffer(role: UserRole | null | undefined, offerAccess: OfferAccessEntry[] = [], offer: string): boolean {
  if (!offer || offer === "all") return false;
  if (isElevatedRole(role)) return true;
  return offerAccess.some((entry) => entry.offer === offer);
}

export function getOfferTabsForUser(
  role: UserRole | null | undefined,
  offerAccess: OfferAccessEntry[] = [],
  offer: string
): MemberOfferTabId[] {
  if (isElevatedRole(role)) return [...MEMBER_OFFER_TAB_IDS];
  return offerAccess.find((entry) => entry.offer === offer)?.tabs || [];
}

export function canUserAccessOfferTab(
  role: UserRole | null | undefined,
  offerAccess: OfferAccessEntry[] = [],
  offer: string,
  tabId: string
): boolean {
  if (!canUserAccessOffer(role, offerAccess, offer)) return false;
  if (isElevatedRole(role)) return true;
  return getOfferTabsForUser(role, offerAccess, offer).includes(tabId as MemberOfferTabId);
}
